import { NoSsr } from "@mui/base";
import { useTheme } from "@mui/material/styles";
import { theme, useResponsive } from "@product/scmp-sdk";
import { usePrevious } from "@react-hookz/web";
import { useScroll } from "ahooks";
import { useAtomValue } from "jotai";
import type { PageProps } from "next";
import type { CSSProperties, FunctionComponent, ReactNode } from "react";
import { useMemo } from "react";

import { HeaderBottomContainerElementID } from "~/pages/_app/consts";

import { appBarAdSlotAtom, appBarAtom } from "./atoms";
import { DynamicAdSlot } from "./dynamics";
import type { AppBarVariant } from "./helpers";
import { useAppBarSetStickyHeight } from "./hooks";
import {
  AdSlotContainer,
  Container,
  FullWidthBackground,
  HeaderBottomContainer,
  StyledHeader,
  StyledPostiesBrochureHeader,
  StyledPostiesContentHeader,
} from "./styles";
import type { AppBarConfiguration } from "./types";

type Props = {
  children?: ReactNode;
  className?: string;
  hideTargetChildren?: ReactNode;
  pageProps?: PageProps;
};

export const AppBar: FunctionComponent<Props> = ({ children, className, pageProps }) => {
  const { adSlotHeight, bindAdSlotContainer, responsiveAdSlot, stickyStyle } = useResponsiveAdSlot(
    pageProps?.appBarConfiguration,
  );

  const hideElementOnScrollEffect = useStickyWithHideElementAfterScrollThreshold(200, adSlotHeight);
  const stickyWithScrollAwayElement = useStickyWithScrollAwayElement(adSlotHeight);

  const styles: CSSProperties = {
    ...(stickyStyle === "withHideElementAfterScrollThreshold"
      ? hideElementOnScrollEffect.targetContainerStyles
      : stickyWithScrollAwayElement.targetContainerStyles),
  };

  const { reference } = useAppBarSetStickyHeight();

  const appBarValue = useAtomValue(appBarAtom);
  const appBarVariant = useMemo<AppBarVariant>(
    () =>
      appBarValue?.scrollOverVariant ?? pageProps?.appBarConfiguration?.variant ?? "genericLight",
    [appBarValue?.scrollOverVariant, pageProps?.appBarConfiguration?.variant],
  );

  const renderHeader = () => {
    switch (appBarVariant) {
      case "posties":
        return <StyledPostiesContentHeader />;
      case "postiesBrochure":
        return <StyledPostiesBrochureHeader />;
      case "style":
        // TODO: Handle style article app bar theme
        return <StyledHeader headerConfiguration={pageProps?.headerConfiguration} />;
      default:
        return <StyledHeader headerConfiguration={pageProps?.headerConfiguration} />;
    }
  };

  return (
    <Container
      $appBarVariant={appBarVariant}
      className={className}
      ref={reference}
      style={{ ...styles }}
    >
      <FullWidthBackground />
      <AdSlotContainer {...bindAdSlotContainer}>
        <NoSsr>{responsiveAdSlot}</NoSsr>
      </AdSlotContainer>
      {children}
      {renderHeader()}
      <HeaderBottomContainer id={HeaderBottomContainerElementID} />
    </Container>
  );
};

AppBar.displayName = "AppBar";

const adSlotVariants = {
  large: {
    height: 250,
    padding: 20,
  },
  small: {
    height: 100,
    padding: 10,
  },
} as const;

const useResponsiveAdSlot = (configuration?: AppBarConfiguration) => {
  const bindAdSlotContainer = useMemo(() => {
    const largeStyles = {
      $height: adSlotVariants.large.height,
      $isVisible: configuration?.hasDesktopAd,
      $padding: adSlotVariants.large.padding,
    };
    const smallStyles = {
      $height: adSlotVariants.small.height,
      $isVisible: configuration?.hasMobileAd,
      $padding: adSlotVariants.small.padding,
    };
    return {
      $responsiveVariants: {
        desktopUp: largeStyles,
        mobileUp: smallStyles,
        tabletUp: smallStyles,
      },
    };
  }, [configuration?.hasDesktopAd, configuration?.hasMobileAd]);

  const isDesktopUp = useResponsive(theme.breakpoints.up("desktop"));
  const adSlotValue = useAtomValue(appBarAdSlotAtom);
  const { adSlotHeight, responsiveAdSlot, stickyStyle } = useMemo(() => {
    switch (true) {
      case isDesktopUp:
        return {
          adSlotHeight: adSlotValue?.desktop
            ? adSlotVariants.large.height + adSlotVariants.large.padding * 2
            : 0,
          responsiveAdSlot: adSlotValue?.desktop ? (
            <DynamicAdSlot
              {...adSlotValue.desktop}
              sizes={[[970, 250], [728, 90], [970, 90], "fluid"]}
            />
          ) : null,
          stickyStyle: "withScrollAwayElement" as const,
        };
      default:
        return {
          adSlotHeight: adSlotValue?.mobile
            ? adSlotVariants.small.height + adSlotVariants.small.padding * 2
            : 0,
          responsiveAdSlot: adSlotValue?.mobile ? (
            <DynamicAdSlot
              {...adSlotValue.mobile}
              sizes={[[300, 100], [320, 100], [300, 50], [320, 50], "fluid"]}
            />
          ) : null,
          stickyStyle: "withHideElementAfterScrollThreshold" as const,
        };
    }
  }, [adSlotValue?.desktop, adSlotValue?.mobile, isDesktopUp]);

  return {
    adSlotHeight,
    bindAdSlotContainer,
    responsiveAdSlot,
    stickyStyle,
  };
};

// This effect will hide the element with transition after the scroll container scroll passes a threshold
const useStickyWithHideElementAfterScrollThreshold = (
  scrollThreshold: number,
  targetElementHeight: number,
) => {
  const theme = useTheme();
  const position = useScroll();
  const containerElementScrollOffsetY = position?.top ?? 0;
  const perviousTargetElementHeight = usePrevious(targetElementHeight);

  const shouldHidden = containerElementScrollOffsetY > scrollThreshold;
  const shouldPauseTransition = perviousTargetElementHeight !== targetElementHeight;

  const targetContainerStyles: CSSProperties = {
    position: "sticky",
    transform: shouldHidden ? `translateY(-${targetElementHeight}px)` : "none",
    transition: shouldPauseTransition
      ? "none" // eslint-disable-next-line etc/no-internal
      : theme.transitions.create("transform", {
          duration: theme.transitions.duration.standard,
        }),
  };

  return {
    targetContainerStyles,
  };
};

// This effect will making the container sticky after the scroll away element is complete scroll passed in the scroll container
const useStickyWithScrollAwayElement = (scrollAwayElementHeight: number) => {
  const position = useScroll();
  const containerElementScrollOffsetY = position?.top ?? 0;

  const shouldStick =
    containerElementScrollOffsetY > 0 && containerElementScrollOffsetY >= scrollAwayElementHeight;

  const targetContainerStyles: CSSProperties = {
    position: shouldStick ? "sticky" : "relative",
    transform: shouldStick ? `translateY(-${scrollAwayElementHeight}px)` : "none",
  };

  return {
    targetContainerStyles,
  };
};
